<template>
  <div class="flex flex-col flex-auto w-full relative bg-black">
    <template v-if="productInfo">
      <LazyUiBreadcrumbs :class="{ '!text-white': !filteredImages?.length }" />
      <div class="container lg:!px-0 flex w-full flex-col md:mb-10 lg:flex-row">
        <div
          v-if="isEngagementRing"
          class="py-5 text-center text-xl font-light text-gray-100 lg:hidden"
        >
          {{ productTitle }}
        </div>

        <div
          class="w-full flex-auto overflow-hidden max-md:-mx-4 max-md:w-[calc(100%+2rem)] lg:flex-[0_0_66.66%] lg:max-w-[calc(100%-480px)]"
          :class="isEngagementRing ? 'border-gray-30 max-lg:border-b' : ''"
        >
          <div ref="leftSide">
            <div class="relative lg:hidden">
              <PagesProductAssetsSlider
                :images="images"
                :show-arrows="lgAndUp"
                :product-data="productInfo as ProductData"
                :is-diamond="false"
                :class="isEngagementRing ? 'mb-0' : 'mb-0'"
              />
            </div>
            <div class="max-lg:hidden">
              <PagesProductAssetsGrid
                :images="filteredImages"
                :is-diamond="false"
                :product-data="productInfo as ProductData"
              />
              <ExtraAsset
                v-if="lastOddImage"
                :asset="lastOddImage"
                :product-data="productInfo as ProductData"
              />
            </div>
          </div>
        </div>

        <div
          ref="rightSide"
          class="w-full flex-shrink-0 lg:mx-auto max-lg:mt-4 lg:px-10 lg:py-15 py-8 lg:sticky lg:top-[var(--header-height)] lg:self-start"
          :class="
            isEngagementRing
              ? 'lg:max-w-[480px] xl:max-w-[644px]'
              : 'lg:max-w-[480px]'
          "
        >
          <ProductEngagementProductInfo
            v-if="isEngagementRing"
            :seo-title="seoTitle"
            :product-title="productTitle"
            :current-configuration="currentConfiguration"
            :in-wishlist="
              Boolean(
                inWishlist({
                  sku: configurationInfo?.configuration_id || '',
                  engraving: {},
                }),
              )
            "
            :discount-value="configurationDiscountValue"
            :product-price="configurationPrice"
            :final-product-price="finalConfigurationPriceValue"
            :filters-pdp-many-in-one="filtersPDPManyInOne"
            :filtered-materials-by-categories="
              filteredMaterialsByCategoriesWithDisabled
            "
            :result="result"
            :additional-options="additionalOptions"
            :add-to-cart-loading="addToCartLoading"
            :details-maped-data="detailsMappedData"
            :product-description="productDescription"
            :shipping-returns-accordion-data="shippingReturnsAccordionData"
            :has-unavailable-material="hasUnavailableMaterial"
            :is-out-of-stock="isConfigurationOutOfStock"
            :product-start-at-price="productStartAtPrice"
            @add-to-cart="onAddToCart(null, true)"
            @optionsFilter="onOptionsFilterClick($event.slug, $event.item)"
            @filter="onFilterClick($event.slug, $event.item)"
            @updateWishlist="onUpdateWishlist"
          />
          <ProductRegularProductInfo
            v-else
            :seo-title="seoTitle"
            :product-title="productTitle"
            :current-configuration="currentConfiguration"
            :in-wishlist="
              Boolean(
                inWishlist({
                  sku: configurationInfo?.configuration_id || '',
                  engraving: {},
                }),
              )
            "
            :discount-value="configurationDiscountValue"
            :product-price="configurationPrice"
            :final-product-price="finalConfigurationPriceValue"
            :filters-pdp-many-in-one="filtersPDPManyInOne"
            :filtered-materials-by-categories="
              filteredMaterialsByCategoriesWithDisabled
            "
            :result="result"
            :additional-options="additionalOptions"
            :add-to-cart-loading="addToCartLoading"
            :details-maped-data="detailsMappedData"
            :product-description="productDescription"
            :shipping-returns-accordion-data="shippingReturnsAccordionData"
            :has-unavailable-material="hasUnavailableMaterial"
            :product-start-at-price="productStartAtPrice"
            :is-out-of-stock="isConfigurationOutOfStock"
            @add-to-cart="onAddToCart(null, true)"
            @options-filter="onOptionsFilterClick($event.slug, $event.item)"
            @filter="onFilterClick($event.slug, $event.item)"
            @updateWishlist="onUpdateWishlist"
          />
        </div>
      </div>

      <ProductListCarousel
        v-if="(productInfo as ProductData).related?.length"
        :products="
          (productInfo as ProductData).related as unknown as CatalogData[]
        "
        :title="$t('styleItWith')"
      />

      <BlocksBlockRecentlyView
        v-if="recentlyViewData"
        :data="recentlyViewData"
      />

      <!-- <DialogsImagePreviewDialog
      v-model="openImagePreview"
      :src="activeImage?.url || ''"
    /> -->
      <DialogsPurchaseProtection v-model="openPurchaseDialog" />
    </template>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useElementSize } from "@vueuse/core";
import { object, string } from "yup";
import { useForm } from "vee-validate";
import { FetchError } from "ofetch";
import {
  BlockData,
  CatalogData,
  EmbedAddToCartData,
  EmbedWishListData,
  ProductData,
  RichTextModuleData,
  ScriptData,
} from "ecom-types";

import { sizesSlugs } from "@/configs";
import ExtraAsset from "~/components/product/ExtraAsset.vue";

// TODO: Need to replace to MF
interface ImageData {
  alt?: string;
  url: string;
  type?: string;
  listType?: string;
}

const route = useRoute();
const router = useRouter();
const config = useRuntimeConfig();
const i18nHead = useLocaleHead({
  addSeoAttributes: true,
});
const { t, locale } = useI18n();

const { addToCart, cartItems, cartInfo } = useCart();
const { setBreadcrumbs, findBreadcrumb } = useBreadcrumbs();
const { inWishlist, updateWishlist } = useWishlist();
const generalStore = useGeneralStore();
const checkoutStore = useCheckoutStore();
const { openSystemDialog } = useSystemDialogs();
const { setProductPageLdSchema } = useJsonLdSchema();
// const { initStripeSource, createAffirmMessageElement } = useCheckout();
const localePath = useLocalePathPolyfill();
const generalInternalStore = useGeneralInternalStore();

const { getIsIndivLoaded } = storeToRefs(generalStore);

const { setRecentlyViewed } = useRecentReview();

const { lgAndUp } = useAppBreakpoints();

const {
  addToCartEvent,
  visitEvent,
  eventsPriceFormat,
  dynamicParamsEvent,
  viewItemEvent,
} = useEvents();

const openPurchaseDialog = ref(false);
const isIndivInited = ref(false);
const addToCartLoading = ref(false);
const isMounted = ref(false);

const { headerMenu } = useGeneralStore();

const { leftSide, rightSide, stickyOffsetTop, stickySide } = useSticky(155);

const { $api } = useNuxtApp();

const [{ data: productResponse }, { data: blocksResponse }] = await Promise.all(
  [
    useAsyncCachedData(route.params.slug as string, () =>
      $api.products.getProductByIdOrSlug(route.params.slug as string),
    ),
    useAsyncCachedData("pdp-blocks", async () => {
      const [recentlyViewResponse, shippingReturnsPdpModuleResponse] =
        await Promise.all([
          // $api.blocks.getBlock("home-recently-view"),
          null,
          $api.blocks.getBlock("shipping-returns-pdp-module"),
        ]);

      return {
        recentlyViewResponse,
        shippingReturnsPdpModuleResponse,
      };
    }),
  ],
);

const {
  currentSku,
  result,
  additionalOptions,
  filteredMaterialsByCategories,
  hasUnavailableMaterial,
  filteredMaterialsByCategoriesWithDisabled,
  filtersPDPManyInOne,
  onFilterClick,
  onOptionsFilterClick,
  allSelectedMaterials,
  productInfo,
  configurationInfo,
  productTitle,
  productDescription,
  materialCategoriesDictionary,
  dynamicProductTitle,
  finalConfigurationPriceValue,
  configurationDiscountValue,
  oldConfigurationSKU,
  configurationPrice,
  configurationError,
  images,
  SizeMaterialCategory,
  isConfigurationOutOfStock,
  isForceToPay,
  productStartAtPrice,
} = usePdp(sizesSlugs, { pdpFilterOptions: { globalsSortOrder: true } });

productInfo.value = productResponse.value!.data;

const { data: currentConfiguration, error: currentConfigurationError } =
  await useAsyncCachedData(
    currentSku,
    () => {
      return $api.products.getConfiguration(currentSku);
    },
    { watch: [currentSku] },
  );

const { detailsMappedData } = useProductDetails();

const { validate } = useProductValidation();

const isEngagementRing = computed(() => {
  return false;
  // TODO: Delete computed and remove from the template when we are sure that there will be no such products
  // return (
  //   productData?.categories?.some(
  //     (el) => el.slug === staticProductCategoriesSlugs.engagement,
  //   ) || false
  // );
});

const lastOddImage = computed<ImageData | null>(() => {
  const files = images.value;
  if (files?.length % 2 !== 0) {
    return files[files.length - 1];
  }
  return null;
});

const filteredImages = computed<ImageData[]>(() => {
  return lastOddImage.value ? images.value.slice(0, -1) : images.value;
});

const shippingReturnsAccordionData = computed(() => {
  const title =
    blocksResponse.value?.shippingReturnsPdpModuleResponse?.data
      ?.currentTranslation?.content?.title || t("shippingAndFaq");
  const content =
    (
      blocksResponse.value?.shippingReturnsPdpModuleResponse
        ?.data as BlockData<RichTextModuleData>
    )?.currentTranslation?.content?.content || "";
  return {
    title,
    content,
  };
});

const recentlyViewData = computed(() => {
  return blocksResponse.value?.recentlyViewResponse?.data?.currentTranslation
    ?.content;
});

watch(
  [() => currentConfigurationError.value, isMounted],
  ([val, mountedVal]) => {
    if ((val as FetchError)?.data?.errors?.[0]?.code === 404) {
      if (
        !oldConfigurationSKU.value &&
        (!productInfo.value?.default_sku ||
          productInfo.value?.default_sku === currentSku.value)
      ) {
        if (process.client) {
          if (!mountedVal) {
            return;
          }
          showError({
            statusCode: 404,
            statusMessage: `No such sku and no default sku`,
            fatal: true,
          });
        } else {
          showError({
            statusCode: 404,
            statusMessage: `No such sku and no default sku`,
            fatal: true,
          });
        }
        return;
      }
      if (!mountedVal) {
        return;
      }
      openSystemDialog({
        type: "alert",
        title: t("noSuchConfiguration"),
        text: t(
          "sorryButTheSelectedConfigurationDoesNotExistPleaseChooseAnyOther",
        ),
      });

      currentSku.value =
        oldConfigurationSKU?.value || productInfo.value?.default_sku || "";
      configurationError.value = true;
    }
  },
  { immediate: true },
);

watch(
  currentConfiguration,
  (val) => {
    configurationInfo.value = val?.data || null;
  },
  {
    immediate: true,
  },
);

watch(
  isForceToPay,
  async (val) => {
    if (!isConfigurationOutOfStock.value && val) {
      generalInternalStore.systemLoader = true;
      await onAddToCart(null, false);
      generalInternalStore.systemLoader = false;
    }
  },
  {
    immediate: true,
  },
);

// watchEffect(() => {
//   if (
//     (configurationDiscountValue.value || configurationPrice.value) &&
//     checkoutStore.stripe.source &&
//     configurationInfo.value &&
//     isMounted.value
//   ) {
//     setTimeout(() => {
//       createAffirmMessageElement(
//         (configurationDiscountValue.value || configurationPrice.value || 0) *
//           100,
//       );
//     }, 150);
//   }
// });

const { setNewBreadcrumbs } = useFullBreadcrumbs();

setNewBreadcrumbs();

setProductPageLdSchema({
  name: productTitle,
  images,
  product: productInfo as Ref<ProductData>,
  price: configurationPrice,
  sku: configurationInfo.value?.configuration_id || "",
  description: productDescription,
});

const script = ref<Ref[]>([]);
const embedScript = ref<ScriptData>({
  hid: "extscript",
  // src: `http://localhost:4173/embedded.js?q=${Date.now()}`,
  src: `https://pdp3.devsyqor.com/embedded.js?q=${Date.now()}`,
  crossorigin: true,
  defer: true,
  onload: () => {
    generalStore.setIndevLoaded();
  },
});

if (productInfo.value?.embedded) {
  script.value.push(embedScript);
}

useHead(() => {
  if (i18nHead.value.link && config.public.domain) {
    const canonicalLink = i18nHead.value.link.find(
      (link) => link.rel === "canonical",
    );
    if (canonicalLink) {
      canonicalLink.href = config.public.domain + route.fullPath;
    }
  }

  return {
    meta: [
      ...metaGenerator({
        title:
          productInfo.value?.currentTranslation?.meta_title ||
          productTitle.value ||
          "",
        description:
          productInfo.value?.currentTranslation?.meta_description ||
          productDescription.value ||
          "",
        image: productInfo.value?.currentTranslation?.og_image?.url || "",
        url:
          productInfo.value?.currentTranslation?.meta_url ||
          config.public.domain + route.path,
      }),
      ...(i18nHead.value.meta || []),
    ],
    htmlAttrs: {
      lang: i18nHead.value.htmlAttrs!.lang,
    },
    link: [...(i18nHead.value.link || [])],
    // title:
    //   productInfo.value?.currentTranslation?.meta_title || productTitle.value || "",
    script,
  };
});

const { seoTitle, seoDescription } = usePageSeo();

useSeoMeta({
  title: () => seoTitle.value,
  ogTitle: () => seoTitle.value,
  description: () =>
    productInfo.value?.currentTranslation?.meta_description ||
    seoDescription.value ||
    "",
  ogDescription: () =>
    productInfo.value?.currentTranslation?.meta_description ||
    seoDescription.value ||
    "",
});

onMounted(async () => {
  visitEvent();
  setViewItemEvent(configurationInfo.value);
  isMounted.value = true;

  if (!isIndivInited.value && getIsIndivLoaded.value) {
    await window.confm8ht77.init({
      customer_number: productInfo.value?.customer_number || "",
      title: productTitle.value || "",
      default_sku: productInfo.value?.default_sku || "",
      // translations: this.$i18n.messages[this.$i18n.locale].configurator,
      discount:
        productInfo.value?.sale_value && productInfo.value?.sale
          ? productInfo.value.sale_value
          : null,
      currency: "USD",
      // lang: process.env.LOCALE,
      size_guide_link:
        productInfo.value?.currentTranslation?.size_chart?.url || null,
      // sticky_offset: 64,
      pdpAddToBag: onAddToCart,
      onWishlist,
      // onCompare: this.onCompare,
    });
    isIndivInited.value = true;
  }
  onSetRecentlyViewed();
  // await initStripeSource();
});

onBeforeUnmount(() => {
  if (window?.confm8ht77?.destroy) {
    window.confm8ht77.destroy();
  }
  onSetRecentlyViewed();
});

async function onAddToCart(
  data?: EmbedAddToCartData | null,
  openCardDrawer?: boolean,
) {
  const { valid } = await validate();

  if (!valid || !productInfo.value) {
    return;
  }

  addToCartLoading.value = true;
  try {
    const images: string[] = [];
    let images_ids: number[] = [];
    const options: { [key: string]: string | number | object } = {};
    if (productInfo.value?.embedded) {
      options.cost = data?.cost || 0;
      options.engraving_options = JSON.stringify(data?.engraving);
      if (data?.images?.length) {
        images.push(...data.images);
      }
      options.configuration_id = data?.configuration_id || "";
    } else {
      const imageIdListIds = configurationInfo.value?.medias?.reduce(
        (acc, item) => {
          if (item?.position?.includes("PDP") && item?.file?.id) {
            acc.push(item.file.id);
          }
          return acc;
        },
        [] as number[],
      );
      let image: string | null = null;
      if (!imageIdListIds?.length) {
        image = await mergeImages([
          { src: configurationInfo.value?.medias?.[0]?.file?.url },
        ]);
      }
      if (image) {
        images.push(image);
      } else if (imageIdListIds?.length) {
        images_ids = imageIdListIds;
      }
    }
    if (Object.values(additionalOptions.value).length) {
      options.materials = Object.entries(additionalOptions.value).map(
        ([key, val]) => {
          return {
            sku: val?.sku,
            title: val?.currentTranslation?.title,
            material_category_title: filteredMaterialsByCategories.value.find(
              (el) => el.slug === key,
            )?.currentTranslation?.title,
            material_category_id: val?.material_category_id,
            material_category_sku: productInfo.value?.material_categories?.find(
              (category) => category.id === val?.material_category_id,
            ),
          };
        },
      );
    }
    await addToCart(
      {
        configuration_id: currentSku.value,
        vendor_model: productInfo.value?.vendor_model,
        images,
        images_ids,
        options,
      },
      openCardDrawer,
    );
    addToCartEvent({
      product_id: productInfo.value.id.toString(),
      configuration_id: configurationInfo.value?.configuration_id || "",
      collection_id: productInfo.value?.collections?.[0]?.id,
      product_sku: productInfo.value?.vendor_model,
      product_price: configurationPrice.value,
      productData: productInfo.value,
      configurationData: configurationInfo.value || undefined,
      productIndex: cartItems.value.length,
    });
    if (isForceToPay.value && cartInfo.value) {
      await router.replace(
        localePath({
          path: `/checkout/${cartInfo.value.unique_id}`,
        }),
      );
    }
  } catch (error) {
    if (error instanceof ImageMergeError) {
      openSystemDialog({
        type: "alert",
        title: `${t("error")}!`,
        text: `${t("failedLoadImage")}!`,
      });
    }
  } finally {
    addToCartLoading.value = false;
    generalInternalStore.systemLoader = false;
  }
}

async function onWishlist(data: EmbedWishListData) {
  const { valid } = await validate();

  if (!valid || !productInfo.value) {
    return;
  }

  await updateWishlist({
    ...data?.value,
    images: isArray(data.value.images)
      ? data.value.images
      : [data.value.images || ""],
    slug: productInfo.value?.slug || "",
    materials: productInfo.value?.materials || [],
    material_categories: productInfo.value?.material_categories,
    material_categories_plp_hover_ids:
      productInfo.value?.material_categories_plp_hover_ids,
    translations: productInfo.value.translations,
    active_configurations_count: productInfo.value.active_configurations_count,
    min_cost: productInfo.value.min_cost,
    product_id: productInfo.value.id,
    vendor_model: productInfo.value.vendor_model,
    start_at_cost: productStartAtPrice.value,
    product_configuration_id: configurationInfo.value?.id,
  });
}

async function onUpdateWishlist() {
  if (!productInfo.value) return;
  try {
    // const image = await mergeImages([
    //   { src: configurationInfo.value?.medias?.[0]?.file?.url },
    // ]);
    const image = configurationInfo.value?.medias?.[0]?.file?.url || "";
    await updateWishlist({
      sku: configurationInfo.value?.configuration_id || "",
      images: [image || ""],
      cost: configurationInfo.value?.cost ? +configurationInfo.value.cost : 0,
      slug: productInfo.value?.slug,
      configuration_id: configurationInfo.value?.configuration_id || "",
      engraving: {},
      materials: productInfo.value?.materials || [],
      material_categories: productInfo.value?.material_categories,
      material_categories_plp_hover_ids:
        productInfo.value?.material_categories_plp_hover_ids,
      translations: productInfo.value?.translations,
      active_configurations_count:
        productInfo.value?.active_configurations_count,
      min_cost: productInfo.value.min_cost,
      product_id: productInfo.value.id,
      vendor_model: productInfo.value.vendor_model,
      start_at_cost: productStartAtPrice.value,
      product_configuration_id: configurationInfo.value?.id,
    });
  } catch (error) {
    if (error instanceof ImageMergeError) {
      openSystemDialog({
        type: "alert",
        title: `${t("error")}!`,
        text: `${t("failedLoadImage")}!`,
      });
    }
  }
}

function onSetRecentlyViewed() {
  const currentTranslationId =
    generalStore.getCurrentLanguage(locale.value as string)?.id ||
    generalStore.getDefaultLanguage?.id;

  if (!configurationPrice.value || !images.value?.[0] || !currentSku.value) {
    return;
  }
  if (productInfo.value) {
    const tempData = {
      ...productInfo.value,
      default_config: {
        materials: configurationInfo.value?.materials.map((el) =>
          productInfo.value?.materials?.find((i) => i.id === el),
        ),
      },
      variant_plp_image: {
        cost: configurationPrice.value,
        image: images.value?.[0] || {},
        sku: currentSku.value,
        translations: [
          {
            title: productTitle.value,
            description: productDescription.value,
            language_id: currentTranslationId,
          },
        ],
      },
    };

    setRecentlyViewed(tempData as unknown as CatalogData);
  }
}

function setViewItemEvent(data) {
  if (!data || !productInfo.value) {
    return;
  }
  const collection = (productInfo.value as ProductData)?.collections?.[0];

  viewItemEvent(productInfo.value as ProductData, data);
  dynamicParamsEvent("View Product", {
    ecomm_prodid: data?.configuration_id,
    ecomm_pagetype: "product",
    ecomm_totalvalue: eventsPriceFormat(data?.base_cost),
    ecomm_category: collection?.currentTranslation.title || "",
  });
}

function usePageSeo() {
  const seoTitle = computed(
    () =>
      `${productInfo.value?.currentTranslation?.meta_title || productTitle.value} | ${generalStore.settings?.company_name}`,
  );

  const seoDescription = computed(() => {
    return `${t("shop")} ${seoTitle.value} ${t("metaDescription")}`;
  });
  return {
    seoTitle,
    seoDescription,
  };
}

function useFullBreadcrumbs() {
  const navigation = computed(() => {
    return toTree(headerMenu);
  });

  const prevPage =
    typeof router.options.history.state.back === "string"
      ? router.options.history.state.back
      : "";
  const prevPageResolved = router.resolve(prevPage);

  const comeFrom = headerMenu.find(
    (menuEl) =>
      menuEl.url ===
      router.resolve({
        path: prevPageResolved.path,
        hash: prevPageResolved.hash,
      }).fullPath,
  );

  const resolvedPath = router.resolve(comeFrom?.url || "");
  const fullBreadcrumbs = findBreadcrumb(navigation.value, resolvedPath);
  function setNewBreadcrumbs() {
    setBreadcrumbs([
      ...[
        { text: t("home"), href: "/", disabled: false },
        ...(fullBreadcrumbs?.length
          ? fullBreadcrumbs.map((el) => ({ ...el, disabled: false }))
          : []),
      ],
      {
        text: productTitle.value || "",
        href: `/product/${productInfo.value?.slug}`,
        disabled: true,
      },
    ]);
  }
  return { setNewBreadcrumbs };
}

function useSticky(offsetTop: number) {
  const customEvent = ref<Event | null>(null);
  const leftSide = ref<HTMLDivElement>();
  const rightSide = ref<HTMLDivElement>();
  const { height: leftHeight } = useElementSize(leftSide);
  const { height: rightHeight } = useElementSize(rightSide);
  const stickyOffsetTop = ref(offsetTop);
  const stickySide = ref("");

  function setStickySide() {
    if (leftHeight.value && rightHeight.value) {
      if (leftHeight.value + stickyOffsetTop.value > rightHeight.value) {
        stickySide.value = "right";
      } else {
        stickySide.value = "left";
      }
    } else {
      stickySide.value = "left";
    }
  }

  watch(leftHeight, async () => {
    if (customEvent.value) {
      await nextTick();
      setStickySide();
      setTimeout(() => {
        window.dispatchEvent(customEvent.value as Event);
      }, 0);
    }
  });

  watch(rightHeight, async () => {
    if (customEvent.value) {
      await nextTick();
      setStickySide();
      setTimeout(() => {
        window.dispatchEvent(customEvent.value as Event);
      }, 0);
    }
  });

  const unwatch = watchEffect(async () => {
    if (leftSide.value && rightSide.value) {
      customEvent.value = new Event("pdpResizeEvent");

      await nextTick(() => {
        setStickySide();
      });
      unwatch();
    }
  });

  return {
    leftSide,
    rightSide,
    customEvent,
    leftHeight,
    rightHeight,
    stickyOffsetTop,
    setStickySide,
    stickySide,
  };
}

function useProductDetails() {
  const detailsMappedData = computed(() => {
    if (
      !productInfo.value?.currentTranslation?.content?.details_map &&
      !configurationInfo.value?.currentTranslation?.additional_details
    ) {
      return [];
    }
    if (configurationInfo.value?.currentTranslation?.additional_details) {
      const additionalDetails =
        configurationInfo.value.currentTranslation.additional_details;
      return parseAdditionalProperties(additionalDetails);
    }
    return productInfo.value?.currentTranslation?.content?.details_map.map(
      (el) => {
        return {
          property: el.property,
          value: replacePlaceholder(
            el.value,
            (materialSKU: string) =>
              allSelectedMaterials.value?.[
                materialCategoriesDictionary.value?.[materialSKU]
              ]?.currentTranslation?.title || "",
          ),
        };
      },
    );
  });

  watchEffect(() => {
    dynamicProductTitle.value = replacePlaceholder(
      (productInfo.value as ProductData)?.currentTranslation?.dynamic_title ||
        "",
      (materialSKU: string) =>
        allSelectedMaterials.value?.[
          materialCategoriesDictionary.value?.[materialSKU]
        ]?.currentTranslation?.title || "",
    );
  });
  return {
    detailsMappedData,
  };
}

function useProductValidation() {
  const productSchema = computed(() => {
    if (
      SizeMaterialCategory.value &&
      (Object.keys(additionalOptions.value)?.includes(
        SizeMaterialCategory.value.slug,
      ) ||
        Object.keys(result.value)?.includes(SizeMaterialCategory.value.slug))
    ) {
      return object().shape({
        [SizeMaterialCategory.value.slug]: string().required(
          t("pleaseSelectSize"),
        ),
      });
    } else {
      return {};
    }
  });

  const { validate } = useForm({
    validationSchema: productSchema,
  });

  return {
    validate,
  };
}
</script>
